import { Button } from "primereact/button";
import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useState
} from "react";
import { useAppSelector } from "../../../hooks/hooks";
import "./TopBar.scss";
import {
  ITopology,
  useLazyGetTopologyVersionsQuery,
  RuntimeMode,
} from "../../../app/apiSlice";
import { useSecureApplication } from "../../../hooks/KeycloackConnection";
import { TagEnv } from "../../../components/environnement/tagEnv";
import { TopBar } from "../../../components/topBar/topBar";
import { IconMode } from "../../../components/Mode/icon";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSavePipeline } from '../../../hooks/useSavePipeline';
import { useLazyGetCatalogQuery } from '../../libraries/DataLibrarySlice';
import { StartStopStream } from "./stream-toolbar";
import { DeployementSettings } from "./deployementSettings";
import { PipelineState } from "../editorSlice";

// -----------------------------------------------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------------------------------------------

export const TopBarEditor = (props: PropsWithChildren<{ onButtonClick?: (buttonId: string) => void; }>) => {

  const topology = useAppSelector((state) => state.editor.current);

  return (
    <TopBar
      left={topology && <LeftComponent />}
      right={topology && <RightComponent topology={topology} onButtonClick={props.onButtonClick}/>}
    />
  );
};

// -----------------------------------------------------------------------------------------------------------------
// LEFT BAR
// -----------------------------------------------------------------------------------------------------------------

const LeftComponent = () => {

  const navigate = useNavigate();
  const name = useAppSelector((state) => state.editor.current?.name);
  const environnements = useAppSelector((state) => state.app.config.environnements)
  const envCode = useAppSelector((state) => state.editor.current?.environnement);
  const runtimeMode = useAppSelector((state) => state.editor.current?.runtimeMode);
  const runtimes = useAppSelector((state) => state.app.config.runtimeMode);

  const runtime = useMemo(
    () => runtimes.find((runtime) => runtime.name === runtimeMode),
    [runtimes, runtimeMode]
  );

  const envName = useMemo(() => {
    return environnements?.find((env) => env.code === envCode)?.name;
  }, [envCode]);

  return (
    <>
      <div onClick={() => navigate("/treatments")} className="mr-2 cursor-pointer" style={{ color: "#6c757d" }}>
        Traitement
      </div>
      <i style={{ color: "#6c757d" }} className="pi pi-chevron-right mr-2"></i>
      {runtime && (
        <IconMode
          color={runtime.icon.color}
          fontSize="1.1rem"
          type={runtime.icon.type}
          name={runtime.displayName}
        />
      )}
      <b className="mr-2 ml-2">{name}</b>
      {envName && <TagEnv code={envCode} name={envName} />}
    </>
  );
};

// -----------------------------------------------------------------------------------------------------------------
// RIGHT BAR
// -----------------------------------------------------------------------------------------------------------------

const RightComponent = (props: PropsWithChildren<{ topology: ITopology, onButtonClick?: (buttonId: string) => void; }>) => {

  const debugMode = useAppSelector((state) => state.editor.debugMode);
  const [viewSettings, setViewSettings] = useState<boolean>(false);
  const pipelineState = useAppSelector((state) =>  state.editor.pipelineState);

  const isPipelineRunning = (): boolean => {
    return pipelineState !== PipelineState.STOPPED && pipelineState !== PipelineState.DELETED && pipelineState !== PipelineState.COMPLETED && pipelineState !== PipelineState.FAILED;
  }

  return (
    <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
      <StartStopStream topology={props.topology} />
      <Button rounded text
        title={"paramètrer le déploiement"}
        onClick={(_) => setViewSettings(true)}
        icon={"pi pi-cog"}
        disabled={pipelineState !== PipelineState.DELETED && pipelineState !== PipelineState.COMPLETED && pipelineState !== PipelineState.FAILED}
      ></Button>
      <SaveButton disabled={false} />
      {props.topology.runtimeMode === RuntimeMode.BASIC &&
        <Button rounded text
          severity={debugMode ? "danger" : undefined}
          title={"Activer le mode Debug"}
          onClick={() => {props.onButtonClick && props.onButtonClick('debug')}}
          icon={"fas fa-bug"}
          disabled={isPipelineRunning()}
        />
      }
      <DeployementSettings setViewSettings={(val) => setViewSettings(val)} viewSettings={viewSettings}/>
    </div>
  );
}

// -----------------------------------------------------------------------------------------------------------------
// SAVE BUTTON
// -----------------------------------------------------------------------------------------------------------------

export const SaveButton = (props: PropsWithChildren<{ disabled: boolean }>) => {
  const { t } = useTranslation();
  const { savePipeline, saveStatus } = useSavePipeline();
  const [ getCatalog, { data: catalog } ] = useLazyGetCatalogQuery();
  const { hasAnyPerm } = useSecureApplication();
  const [getVersions, { data, ...status }] = useLazyGetTopologyVersionsQuery();
  const topology = useAppSelector((state) => state.editor.current);
  const pipelineChanged = useAppSelector((state) => state.editor.changedTopology);

  useEffect(() => {
    if (topology && !catalog) getCatalog({runtimeType:topology.runtimeMode},true)
  }, [topology]);

  if (saveStatus.isLoading) {
    return (
      <Button rounded text icon={"pi pi-spin pi-spinner"} title={ t('button.save') } />
    );
  }

  const handleClick = () => {
    savePipeline(topology, catalog, topology?.version);
    getVersions({ id: topology?.id });
  }

  return (
    <Button rounded text
      icon={"pi pi-save"}
      title={ t('button.save') + (pipelineChanged ? ' • ' + t('editor.unsaved-pipeline') : '') }
      onClick={handleClick}
      disabled={ !hasAnyPerm('pipelines.all', 'pipelines_upd') || !catalog || props.disabled}>
        {pipelineChanged && <i className="pi pi-circle-fill modification-indicator"></i>}
    </Button>
  );
};

